/** Port kinds on FMS boards. Codes match the firmware strings (ai1, di3, ro2 ...). */
export type InputKind = "ai" | "di";
export type OutputKind = "do" | "ro" | "ao";
export type PortKind = InputKind | OutputKind;

export interface PortRef {
  kind: PortKind;
  /** 1-based channel number, as printed on the board */
  channel: number;
}

/** Analog signal types. The character is what the firmware stores (#Cat / #Cot). */
export type SignalType = "0-20mA" | "4-20mA" | "0-10V" | "2-10V" | "0-5V";

export type DeviceModel = "multipro" | "mini";

/** Port counts per model (from the firmware channel range checks). */
export const PORT_COUNTS: Record<DeviceModel, Record<PortKind, number>> = {
  // MultiPro: do5 is the fan output; it is switchable but not a field output.
  multipro: { ai: 8, di: 6, do: 5, ro: 4, ao: 8 },
  mini: { ai: 2, di: 1, do: 0, ro: 1, ao: 0 },
};

/** Everything a device can send up, after the byte stream is split on the X terminator. */
export type Uplink =
  | { type: "heartbeat" } // DUMMYX, sent about once a minute; reply ConnectedxX
  | { type: "handshake-failed" } // HANDSHX: device lost the Connected reply and reconnected
  | { type: "power-lost" } // POWERX
  | { type: "initialise" } // INITIALISEX: device (re)started its dashboard
  | { type: "firmware"; version: string; model: DeviceModel | "unknown" }
  | { type: "radio"; metric: "rssi" | "snr"; values: number[] }
  | { type: "radio-setting"; setting: "lcc" | "lct"; value: number }
  | { type: "analog-input"; channel: number; raw: number }
  /** `periodic`: from the v2 status block (FF 90), sent for every input in every report. */
  | { type: "digital-input"; channel: number; value: 0 | 1; periodic?: true }
  | { type: "pulse-count"; channel: number; count: number }
  | { type: "output-state"; kind: "do" | "ro"; channel: number; on: boolean; periodic?: true }
  | { type: "output-pulse-state"; kind: "do" | "ro"; channel: number; on: boolean }
  | { type: "analog-output"; channel: number; raw: number }
  | { type: "name-ack"; port: PortRef; crc: number }
  | {
      type: "analog-config-ack";
      direction: "input" | "output";
      field: "type" | "unit" | "low" | "high";
      channel: number;
      value: string | number;
    }
  | { type: "pulse-mode-ack"; channel: number; mode: "pulse" | "normal" }
  | { type: "pulse-interval-ack"; seconds: number }
  | { type: "enable-ack"; port: PortRef; enabled: boolean }
  | { type: "rule-saved"; slot: number }
  | { type: "timer-saved"; slot: number }
  | { type: "rules-reset"; slot: number | "all" }
  | { type: "timers-reset"; slot: number | "all" }
  | { type: "rule-error" }
  | { type: "timer-expired"; slot: number; output: PortRef }
  | { type: "rule-executed"; slot: number; output: PortRef }
  | { type: "temperature"; celsius: number }
  | { type: "rs485"; hex: string }
  | { type: "compass"; degrees: number }
  | { type: "gps"; fix: GpsFix | null; raw: string }
  // Payload v2 only (V0.67+): what the board says about itself and settings v1 never reported.
  | { type: "info"; info: Record<string, string | number> }
  | { type: "slot"; slot: string } // running firmware slot A / B ("" without the bootloader)
  | { type: "threshold"; channel: number; low: number | null; high: number | null } // AI alarm thresholds, raw counts
  | { type: "threshold-state"; channel: number; state: "normal" | "low" | "high" }
  | { type: "link-loss-setting"; kind: "do" | "ro"; channel: number; off: boolean }
  | { type: "setting"; name: "report-interval" | "lost-link-time" | "analog-hysteresis"; value: number }
  | { type: "rs485-piece"; index: number; last: boolean; hex: string } // RS485 replies arrive in pieces
  | { type: "safety-rule-saved"; rule: number; crc: number } // crc 0 = refused or deleted
  | { type: "safety-rule-event"; rule: number; fired: boolean }
  // V0.75+: answer to turning a rule on/off/off-for-a-while/reset, or to asking (FF C4).
  // crc is the rule's 41 bytes with the "on" flag set (0 = no rule in that slot).
  | { type: "safety-rule-status"; rule: number; enabled: boolean; active: boolean; latched: boolean; offMinutesLeft: number; crc: number }
  // MultiPro V0.77+ reading its own RS485 Modbus device (modbus-v2.ts): a channel's value as the
  // device sent it, why it has none, and the board's set-up (setup null = channel not used).
  | { type: "modbus-value"; channel: number; value: number }
  | { type: "modbus-error"; channel: number; error: number }
  | { type: "modbus-channel"; channel: number; setup: import("./modbus-v2.ts").BoardModbusChannel | null }
  | { type: "modbus-serial"; serial: import("./modbus-v2.ts").BoardModbusSerial }
  | { type: "unknown"; text: string };

export interface GpsFix {
  lat: number;
  lon: number;
  satellites: number;
  hdop: number;
  altitudeM: number | null;
  utc: string;
}

export type UplinkType = Uplink["type"];
